import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Server, Globe2, Wrench, Clock, CheckCircle2, ArrowRight } from "lucide-react";

const CATEGORY_ICON = { hosting: Server, domain: Globe2, maintenance: Wrench };

function money(v) {
  return `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABEL = {
  active: { text: "Active", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  due_soon: { text: "Due soon", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  past_due: { text: "Past due", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
  grace_period: { text: "Grace period", cls: "bg-orange-500/10 text-orange-300 border-orange-500/30" },
  suspension_pending: { text: "Suspension pending", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  suspended: { text: "Suspended", cls: "bg-slate-700/40 text-slate-400 border-slate-600" },
  cancelled: { text: "Cancelled", cls: "bg-slate-800 text-slate-500 border-slate-700" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { text: status, cls: "bg-slate-800 text-slate-400 border-slate-700" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${s.cls}`}>{s.text}</span>;
}

export default function ClientPortalBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/portal");
      setData(data);
    } catch (e) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const payNow = async (inv) => {
    setPaying(inv.invoice_id);
    try {
      const { data } = await api.post(`/invoices/${inv.invoice_id}/pay`, {
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Checkout URL missing");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
    } finally {
      setPaying(null);
    }
  };

  const services = data?.services || [];
  const openInvoices = data?.open_invoices || [];
  const paidInvoices = data?.paid_invoices || [];

  const outstanding = openInvoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const nextService = services[0];

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="client-billing">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-100">Billing & Payments</h1>
          <p className="text-sm text-slate-400">Your invoices, recurring services, receipts and upcoming renewals.</p>
        </div>

        {/* Top summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-testid="portal-outstanding">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Outstanding</div>
            <div className="text-2xl font-bold text-amber-300">{money(outstanding)}</div>
            <div className="text-xs text-slate-500 mt-1">{openInvoices.length} open invoice(s)</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-testid="portal-next-service">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Next renewal</div>
            <div className="text-lg font-semibold text-slate-100">{nextService?.name || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{nextService ? `${(nextService.next_billing_date || "").slice(0, 10)} · ${money(nextService.price)}` : "No active services"}</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-testid="portal-paid-count">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Payments to date</div>
            <div className="text-2xl font-bold text-emerald-300">{paidInvoices.length}</div>
            <div className="text-xs text-slate-500 mt-1">Total paid invoices</div>
          </div>
        </div>

        {/* Open invoices */}
        <section data-testid="portal-open">
          <h2 className="text-sm font-semibold text-slate-100 mb-3">Open invoices</h2>
          {loading ? (
            <div className="text-slate-500 text-sm">Loading…</div>
          ) : !openInvoices.length ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-8 text-center text-slate-500 text-sm">No open invoices. You're all caught up 🎉</div>
          ) : (
            <div className="rounded-xl border border-slate-800 bg-slate-900/30 overflow-hidden">
              <table className="w-full text-left">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950/60">
                  <tr>
                    <th className="p-3">Invoice</th><th className="p-3">Service</th><th className="p-3">Due</th><th className="p-3">Amount</th><th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {openInvoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-b border-slate-800/70 hover:bg-slate-800/30" data-testid={`open-inv-${inv.invoice_id}`}>
                      <td className="p-3 text-sm text-slate-200 font-mono">#{inv.invoice_number || inv.invoice_id.slice(-6)}</td>
                      <td className="p-3 text-sm text-slate-200">{inv.title}</td>
                      <td className="p-3 text-sm text-amber-300">{(inv.due_date || "").slice(0, 10) || "—"}</td>
                      <td className="p-3 text-sm text-slate-100 font-semibold">{money(inv.total)}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => payNow(inv)} disabled={paying === inv.invoice_id} data-testid={`pay-now-${inv.invoice_id}`} className="btn-scale inline-flex items-center gap-1 h-8 px-3 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold disabled:opacity-50">
                          <CreditCard className="w-3 h-3" /> {paying === inv.invoice_id ? "Loading…" : "Pay now"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recurring services */}
        <section data-testid="portal-services">
          <h2 className="text-sm font-semibold text-slate-100 mb-3">Your recurring services</h2>
          {!services.length ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-8 text-center text-slate-500 text-sm">You don't have any active services yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((svc) => {
                const Icon = CATEGORY_ICON[svc.category] || CheckCircle2;
                return (
                  <div key={svc.service_id} className="rounded-xl border border-slate-800 bg-slate-900/30 p-5" data-testid={`portal-svc-${svc.service_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-9 h-9 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><Icon className="w-4 h-4" /></span>
                        <div>
                          <div className="text-sm font-semibold text-slate-100">{svc.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500">{svc.category} · {svc.billing_frequency}</div>
                        </div>
                      </div>
                      <StatusBadge status={svc.status} />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div><div className="text-slate-500">Price</div><div className="text-slate-100 font-semibold">{money(svc.price)}</div></div>
                      <div><div className="text-slate-500">Next date</div><div className="text-slate-100">{(svc.next_billing_date || "").slice(0, 10) || "—"}</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Payment history */}
        <section data-testid="portal-history">
          <h2 className="text-sm font-semibold text-slate-100 mb-3">Payment history</h2>
          {!paidInvoices.length ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-8 text-center text-slate-500 text-sm">No payments yet.</div>
          ) : (
            <div className="rounded-xl border border-slate-800 bg-slate-900/30 overflow-hidden">
              <table className="w-full text-left">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950/60">
                  <tr><th className="p-3">Invoice</th><th className="p-3">Service</th><th className="p-3">Paid on</th><th className="p-3">Amount</th></tr>
                </thead>
                <tbody>
                  {paidInvoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-b border-slate-800/70">
                      <td className="p-3 text-sm text-slate-300 font-mono">#{inv.invoice_number || inv.invoice_id.slice(-6)}</td>
                      <td className="p-3 text-sm text-slate-300">{inv.title}</td>
                      <td className="p-3 text-sm text-emerald-300">{(inv.paid_at || "").slice(0, 10) || "—"}</td>
                      <td className="p-3 text-sm text-slate-100 font-semibold">{money(inv.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
